import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { omitBy, keys, isNil } from 'lodash';

import { EmptyState } from 'components';
import { useCheckedState, useDateRange, useFeatures } from 'hooks';
import {
  ContentApprovalTypes,
  ContentGroupInput,
  SearchContentInput,
  SearchContentQueryVariables,
  SearchContentSortFields,
  SearchContentSortParam,
  SortDirections,
} from 'api';
import { CheckedMode } from 'types/app';

import { Actions, FilterBar, Header, Table, TableHeader } from '../components/PostManagement';
import { useSearchContent } from '../hooks';
import useBulkContentUpdateMutation from '../updaters/useBulkContentUpdate';

const PostManagement: FC = () => {
  const { t } = useTranslation();
  const dateRangeOptions = useDateRange({ includeToday: true });
  const checkedOptions = useCheckedState();
  const { singleGroupPost } = useFeatures();

  const { startDate, endDate } = dateRangeOptions;
  const { checkedIds, checkedMode } = checkedOptions;
  const [sort, setSort] = useState<SearchContentSortParam[]>([
    {
      direction: SortDirections.Desc,
      field: SearchContentSortFields.CreatedAt,
    },
  ]);

  const defaultFilter = {
    approval: ContentApprovalTypes.Submitted,
    created_after: moment(startDate).unix(),
    created_before: moment(endDate).add(30, 'days').unix(),
    sort_by: sort,
  };

  const [filter, setFilter] = useState<SearchContentInput>(defaultFilter);
  const [hasFilters, setHasFilters] = useState(false);

  useEffect(() => {
    const filterKeyCount = keys(omitBy(filter, isNil)).length;
    const defaultKeyCount = keys(defaultFilter).length;
    setHasFilters(filterKeyCount !== defaultKeyCount);
  }, [filter]);

  function clearFilter() {
    setFilter(defaultFilter);
  }

  const variables: SearchContentQueryVariables = {
    search_content: {
      aggregate_content: true,
      approval: filter.approval,
      ...filter,
      sort_by: sort,
    },
  };

  // do not aggregate content in a single post system
  if (singleGroupPost) {
    delete variables.search_content.aggregate_content;
  }

  const queryOptions = useSearchContent(variables);
  const updateContentMutationOptions = useBulkContentUpdateMutation(variables);

  const { isEmpty, results } = queryOptions;
  const actionCount = checkedMode === CheckedMode.OFF ? checkedIds.length : results.length;

  function updateTableColumnSort(sortParams: SearchContentSortParam[]) {
    setSort(sortParams);
  }

  function renderTable() {
    if (isEmpty) {
      return (
        <EmptyState
          name="postManagement"
          actionComponent={
            <Button color="primary" onClick={clearFilter}>
              <Typography variant="subtitle2" align="center">
                {t('admin:clearFilters')}
              </Typography>
            </Button>
          }
        />
      );
    }

    return (
      <Table
        approval={filter.approval}
        sortBy={sort[0]}
        onSort={updateTableColumnSort}
        updateContentMutationOptions={updateContentMutationOptions}
        {...checkedOptions}
        {...queryOptions}
      />
    );
  }

  function getActionVariables() {
    if (checkedMode === CheckedMode.PAGE) {
      return {
        bulk_update: {
          approval: filter.approval,
          content_group_list: results.map(data => ({
            group_id: data.group_ids[0],
            content_id: data.content_id,
          })) as ContentGroupInput[],
        },
      };
    }

    function getGroupId(contentId: string) {
      return results
        .filter(data => data.content_id === contentId)
        .map(data => data.group_ids)
        .flat();
    }

    return {
      bulk_update: {
        approval: filter.approval,
        content_group_list: checkedIds.map(id => ({
          group_id: getGroupId(id)[0],
          content_id: id,
        })) as ContentGroupInput[],
      },
    };
  }

  return (
    <Fragment>
      <Box pb={2}>
        <Header />
      </Box>

      <Box pb={3}>
        <FilterBar
          filter={filter}
          setFilter={setFilter}
          hasFilters={hasFilters}
          clearFilter={clearFilter}
          {...dateRangeOptions}
        />
      </Box>

      <Paper>
        <TableHeader
          filter={filter}
          setFilter={setFilter}
          {...checkedOptions}
          actions={
            <Actions
              bulk
              count={actionCount}
              variables={getActionVariables()}
              contents={results}
              updateContentMutationOptions={updateContentMutationOptions}
              {...checkedOptions}
            />
          }
        />
        {renderTable()}
      </Paper>
    </Fragment>
  );
};

export default PostManagement;
